import axios from 'axios';

import { toFiniteCount } from '../../utils/formatters';
import {
  getDisabledImageKeySet,
  getLotActionKey,
  getResultLotNumber,
  normalizeImageKey,
} from '../../utils/testRigHelpers';

export const createLotImageRerunControls = (deps) => {
  const {
    testResults,
    setRerunningLotBusy,
    updateRunLotResultInState,
    normalizeImageKeyList,
    isExpectedLastImageToggleBlock,
    mergeUpdatedRunInState,
    fetchData,
  } = deps;

  const RERUN_TIMEOUT_POLL_INTERVAL_MS = 3000;
  const RERUN_TIMEOUT_POLL_WINDOW_MS = 180000;

  const toggleLotImageDisabled = ({ runId, lotNumber, imageUrl, disable, resultIndex = null }) => {
    const normalizedRunId = String(runId || '').trim();
    const normalizedLot = String(lotNumber || '').trim();
    const normalizedImage = String(imageUrl || '').trim();
    const normalizedResultIndex = Number.isFinite(Number(resultIndex))
      ? Number(resultIndex)
      : null;
    if (!normalizedRunId || !normalizedLot || !normalizedImage) {
      alert('Missing run, lot, or image identifier.');
      return;
    }

    const normalizedImgKey = normalizeImageKey(normalizedImage) || normalizedImage.toLowerCase();
    if (!normalizedImgKey) {
      alert('Invalid image identifier.');
      return;
    }

    updateRunLotResultInState(
      normalizedRunId,
      normalizedLot,
      (resultEntry) => {
        const nextSet = new Set(
          normalizeImageKeyList(Array.from(getDisabledImageKeySet(resultEntry))),
        );
        if (disable) nextSet.add(normalizedImgKey);
        else nextSet.delete(normalizedImgKey);
        return {
          ...resultEntry,
          disabled_image_keys: Array.from(nextSet).sort(),
        };
      },
      { resultIndex: normalizedResultIndex },
    );

    axios
      .post('/test_rig_toggle_lot_image', {
        run_id: normalizedRunId,
        lot_number: normalizedLot,
        image_url: normalizedImage,
        disable: !!disable,
      })
      .then((response) => {
        if (response?.data?.status === 'success') return;
        const failureMessage =
          response?.data?.message || 'Failed to update lot image state.';
        if (!isExpectedLastImageToggleBlock(failureMessage)) {
          alert(failureMessage);
        }
        updateRunLotResultInState(
          normalizedRunId,
          normalizedLot,
          (resultEntry) => {
            const current = new Set(
              normalizeImageKeyList(Array.from(getDisabledImageKeySet(resultEntry))),
            );
            if (disable) current.delete(normalizedImgKey);
            else current.add(normalizedImgKey);
            return {
              ...resultEntry,
              disabled_image_keys: Array.from(current).sort(),
            };
          },
          { resultIndex: normalizedResultIndex },
        );
      })
      .catch((error) => {
        const message =
          error?.response?.data?.message ||
          error.message ||
          'Failed to update lot image state.';
        if (!isExpectedLastImageToggleBlock(message)) {
          alert(message);
        }
        updateRunLotResultInState(
          normalizedRunId,
          normalizedLot,
          (resultEntry) => {
            const current = new Set(
              normalizeImageKeyList(Array.from(getDisabledImageKeySet(resultEntry))),
            );
            if (disable) current.delete(normalizedImgKey);
            else current.add(normalizedImgKey);
            return {
              ...resultEntry,
              disabled_image_keys: Array.from(current).sort(),
            };
          },
          { resultIndex: normalizedResultIndex },
        );
      });
  };

  const rerunSingleLot = async ({ runId, lotNumber }) => {
    const getLotRerunSnapshot = (runs, targetRunId, targetLotNumber) => {
      if (!Array.isArray(runs)) return null;
      const targetRun = String(targetRunId || '').trim();
      const targetLot = String(targetLotNumber || '').trim();
      if (!targetRun || !targetLot) return null;

      const runEntry = runs.find(
        (entry) => String(entry?.run_id || '').trim() === targetRun,
      );
      if (!runEntry || !Array.isArray(runEntry.results)) return null;

      const lotEntry = runEntry.results.find(
        (resultEntry) => String(getResultLotNumber(resultEntry) || '').trim() === targetLot,
      );
      if (!lotEntry || typeof lotEntry !== 'object') return null;

      return {
        attemptsUsed: toFiniteCount(lotEntry?.attempts_used) ?? 1,
        rerunHistoryCount: Array.isArray(lotEntry?.rerun_history)
          ? lotEntry.rerun_history.length
          : 0,
      };
    };

    const hasRerunSnapshotAdvanced = (beforeSnapshot, currentSnapshot) => {
      if (!currentSnapshot) return false;
      if (!beforeSnapshot) return true;
      return (
        currentSnapshot.attemptsUsed > beforeSnapshot.attemptsUsed ||
        currentSnapshot.rerunHistoryCount > beforeSnapshot.rerunHistoryCount
      );
    };

    const waitForRerunAfterGatewayTimeout = async ({
      targetRunId,
      targetLotNumber,
      beforeSnapshot,
    }) => {
      const deadline = Date.now() + RERUN_TIMEOUT_POLL_WINDOW_MS;
      while (Date.now() < deadline) {
        await new Promise((resolve) => {
          setTimeout(resolve, RERUN_TIMEOUT_POLL_INTERVAL_MS);
        });
        try {
          const resultResponse = await axios.get('/get_test_results', {
            params: { run_id: targetRunId },
          });
          const nextRuns = Array.isArray(resultResponse?.data) ? resultResponse.data : [];
          const nextSnapshot = getLotRerunSnapshot(nextRuns, targetRunId, targetLotNumber);
          if (hasRerunSnapshotAdvanced(beforeSnapshot, nextSnapshot)) {
            const updatedRun = nextRuns.find(
              (entry) =>
                String(entry?.run_id || '').trim() === String(targetRunId || '').trim(),
            );
            if (updatedRun) mergeUpdatedRunInState(updatedRun);
            return true;
          }
        } catch (pollError) {
          console.warn('Rerun timeout polling failed:', pollError);
        }
      }
      return false;
    };

    const normalizedRunId = String(runId || '').trim();
    const normalizedLot = String(lotNumber || '').trim();
    if (!normalizedRunId || !normalizedLot) {
      alert('Missing run or lot identifier.');
      return;
    }
    if (!window.confirm(`Rerun lot ${normalizedLot} in run ${normalizedRunId.slice(0, 8)}?`)) {
      return;
    }

    const baselineSnapshot = getLotRerunSnapshot(testResults, normalizedRunId, normalizedLot);
    const actionKey = getLotActionKey(normalizedRunId, normalizedLot);
    setRerunningLotBusy((previous) => ({ ...previous, [actionKey]: true }));

    try {
      const response = await axios.post(
        '/rerun_test_rig_lot',
        { run_id: normalizedRunId, lot_number: normalizedLot },
        { timeout: 300000 },
      );
      if (response?.data?.status === 'success') {
        const returnedRun = response?.data?.run;
        const returnedResult = response?.data?.result;
        if (returnedRun && typeof returnedRun === 'object') {
          mergeUpdatedRunInState(returnedRun);
        } else if (returnedResult && typeof returnedResult === 'object') {
          updateRunLotResultInState(normalizedRunId, normalizedLot, () => returnedResult);
        }
        fetchData({ showLoading: false });
      } else {
        alert(response?.data?.message || 'Failed to rerun lot.');
        fetchData({ showLoading: false });
      }
    } catch (error) {
      const statusCode = Number(error?.response?.status || 0);
      if (statusCode === 504) {
        const completedAfterTimeout = await waitForRerunAfterGatewayTimeout({
          targetRunId: normalizedRunId,
          targetLotNumber: normalizedLot,
          beforeSnapshot: baselineSnapshot,
        });
        if (!completedAfterTimeout) {
          alert(
            'Rerun request hit gateway timeout (504). It may still complete in background. ' +
              'If this repeats, increase reverse-proxy and Gunicorn timeouts.',
          );
        }
      } else {
        const message =
          error?.response?.data?.message || error.message || 'Failed to rerun lot.';
        alert(message);
      }
      fetchData({ showLoading: false });
    } finally {
      setRerunningLotBusy((previous) => {
        const next = { ...previous };
        delete next[actionKey];
        return next;
      });
    }
  };

  return {
    toggleLotImageDisabled,
    rerunSingleLot,
    RERUN_TIMEOUT_POLL_INTERVAL_MS,
    RERUN_TIMEOUT_POLL_WINDOW_MS,
  };
};
